import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  PROMPT_NODE_OPTIONS,
  eventLabel,
  isPromptEditable,
  orderedSystemPromptBlocks,
  parsePromptJson,
  parsePromptStringArray,
  promptLifecycleActions,
  promptReadOnlyMessage,
  promptStatusLabel,
  promptTestInputTemplate,
  promptTestResultState,
} from './prompt-admin-model'

export function PromptManagementPage({ apiClient, flash }) {
  const [nodeKey, setNodeKey] = useState('intent_parse')
  const [subtab, setSubtab] = useState('versions')
  const [versions, setVersions] = useState([])
  const [nodes, setNodes] = useState([])
  const [detail, setDetail] = useState(null)
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [error, setError] = useState('')
  const [testError, setTestError] = useState('')
  const [mutation, setMutation] = useState('')
  const [versionsScope, setVersionsScope] = useState('')
  const [pendingAction, setPendingAction] = useState(null)
  const listRequestRef = useRef(0)
  const detailRequestRef = useRef(0)
  const eventsRequestRef = useRef(0)

  const selectedNode = PROMPT_NODE_OPTIONS.find(item => item.promptNodeKey === nodeKey)
  const selectedScope = promptNodeScope(selectedNode)
  const versionsAreCurrent = versionsScope === selectedScope

  const load = useCallback(async () => {
    const requestId = ++listRequestRef.current
    const requestNode = selectedNode
    const requestScope = promptNodeScope(requestNode)
    setLoading(true)
    setError('')
    setTestError('')
    setVersions([])
    setVersionsScope('')
    try {
      const response = await apiClient.get('/semantic/admin/prompt-configs', {
        params: {
          scenarioKey: requestNode.scenarioKey,
          promptNodeKey: requestNode.promptNodeKey,
        },
      })
      if (requestId !== listRequestRef.current) return
      setVersions(response.data.data.versions || [])
      setVersionsScope(requestScope)
      setNodes(response.data.data.nodes || [])
    } catch (cause) {
      if (requestId !== listRequestRef.current) return
      setError(apiMessage(cause, 'Prompt 版本加载失败'))
    } finally {
      if (requestId === listRequestRef.current) setLoading(false)
    }
  }, [apiClient, selectedNode])

  useEffect(() => {
    setDetail(null)
    setEvents([])
    setSubtab('versions')
    load()
  }, [load])

  const loadEvents = useCallback(async () => {
    if (!versions.length) {
      setEvents([])
      return
    }
    const requestId = ++eventsRequestRef.current
    const requestScope = versionsScope
    setLoading(true)
    setError('')
    try {
      const responses = await Promise.all(
        versions.map(version => apiClient.get(`/semantic/admin/prompt-configs/${version.id}/events`)),
      )
      if (requestId !== eventsRequestRef.current || requestScope !== selectedScope) return
      setEvents(
        responses
          .flatMap(response => response.data.data || [])
          .sort((left, right) => Number(right.createdAt) - Number(left.createdAt)),
      )
    } catch (cause) {
      if (requestId !== eventsRequestRef.current || requestScope !== selectedScope) return
      setError(apiMessage(cause, '操作日志加载失败'))
    } finally {
      if (requestId === eventsRequestRef.current && requestScope === selectedScope) setLoading(false)
    }
  }, [apiClient, selectedScope, versions, versionsScope])

  useEffect(() => {
    if (subtab === 'events') loadEvents()
  }, [subtab, loadEvents])

  async function openDetail(id) {
    const requestId = ++detailRequestRef.current
    const requestScope = selectedScope
    setDetailLoading(true)
    setError('')
    setTestError('')
    try {
      const response = await apiClient.get(`/semantic/admin/prompt-configs/${id}`)
      if (requestId !== detailRequestRef.current || requestScope !== selectedScope) return
      setDetail(prepareDetail(response.data.data))
    } catch (cause) {
      if (requestId !== detailRequestRef.current || requestScope !== selectedScope) return
      setError(apiMessage(cause, 'Prompt 详情加载失败'))
    } finally {
      if (requestId === detailRequestRef.current && requestScope === selectedScope) setDetailLoading(false)
    }
  }

  function selectNode(nextNodeKey) {
    if (nextNodeKey === nodeKey) return
    listRequestRef.current += 1
    detailRequestRef.current += 1
    eventsRequestRef.current += 1
    setVersions([])
    setVersionsScope('')
    setEvents([])
    setDetail(null)
    setError('')
    setTestError('')
    setLoading(true)
    setDetailLoading(false)
    setSubtab('versions')
    setNodeKey(nextNodeKey)
  }

  async function createDraft() {
    await runMutation('create', async () => {
      const response = await apiClient.post('/semantic/admin/prompt-configs/draft', {
        scenarioKey: selectedNode.scenarioKey,
        promptNodeKey: selectedNode.promptNodeKey,
        name: `${selectedNode.label}草稿`,
      })
      await load()
      setDetail(prepareDetail(response.data.data))
      flash('草稿已创建')
    })
  }

  async function runAction(action, version) {
    if (['publish', 'activate', 'archive'].includes(action)) {
      setPendingAction({ action, version })
      return
    }
    await commitAction(action, version)
  }

  async function confirmPendingAction() {
    if (!pendingAction) return
    await commitAction(pendingAction.action, pendingAction.version)
    setPendingAction(null)
  }

  async function commitAction(action, version) {
    await runMutation(`${action}:${version.id}`, async () => {
      const response = await apiClient.post(`/semantic/admin/prompt-configs/${version.id}/${action}`, {})
      await load()
      setDetail(action === 'clone' ? prepareDetail(response.data.data) : null)
      flash({ clone: '已复制为新草稿', publish: '版本已发布', activate: '生效版本已更新', archive: '版本已归档' }[action])
    })
  }

  async function saveDraft() {
    await runMutation(`save:${detail.id}`, async () => {
      const response = await apiClient.patch(`/semantic/admin/prompt-configs/${detail.id}/draft`, {
        changeNote: detail.changeNote,
        systemPromptBlocks: detail.systemPromptBlocks,
        systemRenderOrder: parsePromptStringArray(detail.systemRenderOrderText, 'System 编排顺序'),
        userPromptTemplate: detail.userPromptTemplate,
        variablesSchema: parsePromptJson(detail.variablesSchemaText, '变量声明'),
        outputSchema: parsePromptJson(detail.outputSchemaText, '输出结构'),
      })
      setDetail(prepareDetail(response.data.data))
      await load()
      flash('草稿已保存')
    })
  }

  async function testPrompt(version, inputText) {
    const key = `test:${version.id}`
    setMutation(key)
    setTestError('')
    try {
      const variables = parsePromptJson(inputText, '测试输入')
      const imageUrls = promptTestImageUrls(variables)
      const response = await apiClient.post(`/semantic/admin/prompt-configs/${version.id}/test`, {
        variables,
        ...(imageUrls.length ? { imageUrls } : {}),
      })
      const result = response.data.data
      setDetail(current => current?.id === version.id ? { ...current, lastTestResult: result } : current)
      setVersions(current => current.map(item => item.id === version.id ? { ...item, lastTestResult: result } : item))
      await loadEvents()
      const resultState = promptTestResultState(result)
      flash(resultState.tone === 'success'
        ? 'Prompt 测试通过'
        : resultState.tone === 'warning'
          ? 'Prompt 测试完成，输出结构无效'
          : 'Prompt 测试失败，Provider 返回错误')
    } catch (cause) {
      setTestError(apiMessage(cause, 'Prompt 测试失败'))
    } finally {
      setMutation('')
    }
  }

  async function runMutation(key, work) {
    setMutation(key)
    setError('')
    try {
      await work()
    } catch (cause) {
      setError(apiMessage(cause, '操作失败'))
    } finally {
      setMutation('')
    }
  }

  return (
    <div className="prompt-admin-page">
      {pendingAction ? (
        <PromptLifecycleConfirmModal
          action={pendingAction.action}
          version={pendingAction.version}
          loading={mutation === `${pendingAction.action}:${pendingAction.version.id}`}
          onClose={() => setPendingAction(null)}
          onConfirm={confirmPendingAction}
        />
      ) : null}

      <div className="prompt-node-nav">
        <div className="prompt-node-tabs" role="tablist" aria-label="Prompt 节点">
          {PROMPT_NODE_OPTIONS.map(node => (
            <button
              className={node.promptNodeKey === nodeKey ? 'active' : ''}
              type="button"
              role="tab"
              id={`prompt-node-tab-${node.promptNodeKey}`}
              aria-controls={`prompt-node-panel-${node.promptNodeKey}`}
              aria-selected={node.promptNodeKey === nodeKey}
              tabIndex={node.promptNodeKey === nodeKey ? 0 : -1}
              disabled={Boolean(mutation)}
              key={node.promptNodeKey}
              onClick={() => selectNode(node.promptNodeKey)}
              onKeyDown={handleTabKeyDown}
            >
              {node.label}
              {nodes.find(item => item.promptNodeKey === node.promptNodeKey)?.health?.required ? <span>必需</span> : null}
            </button>
          ))}
        </div>
        <button className="btn primary" type="button" disabled={Boolean(mutation)} onClick={createDraft}>
          {mutation === 'create' ? '创建中' : '新建草稿'}
        </button>
      </div>

      <div
        id={`prompt-node-panel-${nodeKey}`}
        role="tabpanel"
        aria-labelledby={`prompt-node-tab-${nodeKey}`}
        aria-busy={loading || detailLoading}
        tabIndex={0}
      >
        {error ? <div className="banner danger prompt-error" role="alert"><span>{error}</span><button className="link" type="button" onClick={load}>重试</button></div> : null}

        {detailLoading ? <PromptDetailSkeleton /> : detail ? (
          <PromptDetail
            detail={detail}
            apiClient={apiClient}
            mutation={mutation}
            onBack={() => setDetail(null)}
            onChange={setDetail}
            onSave={saveDraft}
            onAction={runAction}
            onTest={testPrompt}
            testError={testError}
          />
        ) : (
          <section className="prompt-workspace">
            <div className="prompt-subtabs" role="tablist" aria-label="Prompt 数据视图">
              <button
                className={subtab === 'versions' ? 'active' : ''}
                type="button"
                role="tab"
                id="prompt-subtab-versions"
                aria-controls="prompt-subpanel-versions"
                aria-selected={subtab === 'versions'}
                tabIndex={subtab === 'versions' ? 0 : -1}
                onClick={() => setSubtab('versions')}
                onKeyDown={handleTabKeyDown}
              >版本列表</button>
              <button
                className={subtab === 'events' ? 'active' : ''}
                type="button"
                role="tab"
                id="prompt-subtab-events"
                aria-controls="prompt-subpanel-events"
                aria-selected={subtab === 'events'}
                tabIndex={subtab === 'events' ? 0 : -1}
                onClick={() => setSubtab('events')}
                onKeyDown={handleTabKeyDown}
              >操作日志</button>
            </div>
            <div
              id={`prompt-subpanel-${subtab}`}
              role="tabpanel"
              aria-labelledby={`prompt-subtab-${subtab}`}
              aria-busy={loading}
              tabIndex={0}
            >
              {error ? null : loading || !versionsAreCurrent ? <PromptTableSkeleton /> : subtab === 'versions' ? (
                <PromptVersionTable versions={versions} mutation={mutation} onOpen={openDetail} onAction={runAction} />
              ) : (
                <PromptEventTable events={events} />
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}

function PromptLifecycleConfirmModal({ action, version, loading, onClose, onConfirm }) {
  const copy = promptActionCopy(action)
  const testState = promptTestResultState(version.lastTestResult)
  const modalClass = `confirm-modal prompt-confirm-modal ${copy.tone === 'danger' ? 'danger' : 'publish'}`
  return (
    <PromptModal title={copy.title} onClose={loading ? undefined : onClose} className={modalClass}>
      <div className="confirm-summary">
        <span>{copy.label}</span>
        <strong>{version.version}</strong>
        {version.changeNote ? <p>{version.changeNote}</p> : null}
      </div>

      <dl className="prompt-confirm-meta" aria-label="版本确认信息">
        <div>
          <dt>当前状态</dt>
          <dd><span className={`prompt-status ${version.isActive ? 'active' : version.status}`}>{promptStatusLabel(version)}</span></dd>
        </div>
        <div>
          <dt>最近测试</dt>
          <dd className={testState.tone}>{testState.label}{version.lastTestResult?.latencyMs ? ` · ${Number(version.lastTestResult.latencyMs)} ms` : ''}</dd>
        </div>
        <div>
          <dt>配置 Hash</dt>
          <dd><code>{version.configHash?.slice(0, 12) || '-'}</code></dd>
        </div>
      </dl>

      <p className="confirm-copy">{copy.description}</p>
      <ul className="confirm-points">
        {copy.points.map(point => <li key={point}>{point}</li>)}
      </ul>
      <div className="modal-actions confirm-actions">
        <button className="btn ghost" type="button" disabled={loading} autoFocus onClick={onClose}>先不处理</button>
        <button className={`btn ${copy.tone}`} type="button" disabled={loading} onClick={onConfirm}>
          {loading ? '处理中' : copy.confirmText}
        </button>
      </div>
    </PromptModal>
  )
}

function PromptModal({ title, children, onClose, className = '' }) {
  useEffect(() => {
    if (!onClose) return undefined
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])
  return (
    <div className="modal-mask" onMouseDown={event => { if (event.target === event.currentTarget) onClose?.() }}>
      <div className={`modal ${className}`} role="dialog" aria-modal="true" aria-label={title} onMouseDown={event => event.stopPropagation()}>
        <div className="modal-head">
          <h2>{title}</h2>
          {onClose ? <button className="icon-btn" type="button" aria-label="关闭" onClick={onClose}>×</button> : null}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  )
}

function promptActionCopy(action) {
  if (action === 'archive') {
    return {
      title: '归档 Prompt 版本',
      label: '从可用版本中移除',
      description: '归档后，这个版本不能再设为生效；历史调用、测试记录和操作日志仍会保留。',
      confirmText: '归档版本',
      tone: 'danger',
      points: ['不会删除版本内容或历史记录', '当前生效版本不可直接归档，请先启用替代版本'],
    }
  }
  if (action === 'activate') {
    return {
      title: '设为生效版本',
      label: '后续调用将使用此版本',
      description: '启用后，当前 Prompt 节点的新请求会使用这个版本；原生效版本会保留为已发布版本。',
      confirmText: '设为生效',
      tone: 'primary',
      points: ['会影响当前节点的后续线上调用', '建议先确认最近测试结果符合预期'],
    }
  }
  return {
    title: '发布 Prompt 版本',
    label: '发布后进入只读版本',
    description: '发布后，这个草稿会成为只读版本；后续调整需要复制为新草稿再修改。',
    confirmText: '发布版本',
    tone: 'primary',
    points: ['发布不会自动设为当前生效版本', '建议发布前确认最近测试为结构有效'],
  }
}

function PromptVersionTable({ versions, mutation, onOpen, onAction }) {
  if (!versions.length) return <div className="prompt-empty"><b>暂无版本</b><span>从新建草稿开始</span></div>
  return (
    <div className="prompt-table-scroll">
      <table className="prompt-version-table">
        <thead><tr><th>版本</th><th>状态</th><th>最近测试</th><th>变更说明</th><th>更新时间</th><th>操作</th></tr></thead>
        <tbody>
          {versions.map(version => (
            <tr key={version.id}>
              <td><button className="prompt-version-link" type="button" onClick={() => onOpen(version.id)}><b>{version.version}</b><span>{version.configHash?.slice(0, 12)}</span></button></td>
              <td><span className={`prompt-status ${version.isActive ? 'active' : version.status}`}>{promptStatusLabel(version)}</span></td>
              <td><PromptTestSummary result={version.lastTestResult} compact /></td>
              <td className="prompt-note-cell">{version.changeNote || '-'}</td>
              <td className="prompt-updated-at nowrap muted">{formatTime(version.updatedAt)}</td>
              <td><PromptRowActions version={version} mutation={mutation} onOpen={onOpen} onAction={onAction} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function PromptRowActions({ version, mutation, onOpen, onAction }) {
  const actions = promptLifecycleActions(version)
  const actionBlocked = Boolean(mutation)
  const reason = actions.publish.reason || actions.archive.reason
  return (
    <div className="prompt-row-action-cell">
      <div className="prompt-row-actions">
        <button className="link" type="button" disabled={actionBlocked} onClick={() => onOpen(version.id)}>查看</button>
        <button className="link" type="button" disabled={actionBlocked} onClick={() => onAction('clone', version)}>复制</button>
        {actions.publish.visible ? <button className="link" type="button" disabled={actionBlocked || !actions.publish.enabled} title={actions.publish.reason || undefined} onClick={() => onAction('publish', version)}>发布</button> : null}
        {actions.activate.visible ? <button className="link" type="button" disabled={actionBlocked || !actions.activate.enabled} onClick={() => onAction('activate', version)}>生效</button> : null}
        {actions.archive.visible ? <button className="link danger-link" type="button" disabled={actionBlocked || !actions.archive.enabled} title={actions.archive.reason || undefined} onClick={() => onAction('archive', version)}>归档</button> : null}
      </div>
      {reason ? <span className="prompt-action-reason">{reason}</span> : null}
    </div>
  )
}

function PromptEventTable({ events }) {
  if (!events.length) return <div className="prompt-empty"><b>暂无操作日志</b></div>
  return (
    <div className="prompt-event-list">
      {events.map(event => (
        <div className="prompt-event-row" key={`${event.id}-${event.createdAt}`}>
          <span className="prompt-event-mark" />
          <div><b>{eventLabel(event.eventType)}</b><span>{event.reason || event.metadata?.version || '-'}</span></div>
          <div><b>{event.operator || '-'}</b><span>{formatTime(event.createdAt)}</span></div>
        </div>
      ))}
    </div>
  )
}

function PromptDetail({ detail, apiClient, mutation, onBack, onChange, onSave, onAction, onTest, testError }) {
  const editable = isPromptEditable(detail)
  const actions = promptLifecycleActions(detail)
  const readOnlyMessage = promptReadOnlyMessage(detail)
  const systemBlocks = orderedSystemPromptBlocks(detail)
  const owner = detail.updatedBy || detail.createdBy || '-'
  const shortHash = detail.configHash?.slice(0, 16) || '-'
  const updateBlock = (key, content) => onChange({
    ...detail,
    systemPromptBlocks: detail.systemPromptBlocks.map(block => block.key === key ? { ...block, content } : block),
  })
  return (
    <section className="prompt-admin-detail">
      <div className="prompt-admin-detail-head">
        <div className="prompt-admin-titlebar">
          <button className="prompt-back" type="button" onClick={onBack} aria-label="返回版本列表">←</button>
          <div className="prompt-admin-title">
            <h2>{detail.name}</h2>
            <div className="prompt-admin-meta">
              <span className={`prompt-status ${detail.isActive ? 'active' : detail.status}`}>{promptStatusLabel(detail)}</span>
              <code>{detail.version}</code>
              <code>{shortHash}</code>
              <span>{owner}</span>
            </div>
          </div>
        </div>
        <div className="prompt-detail-actions">
          <button className="btn ghost" type="button" disabled={Boolean(mutation)} onClick={() => onAction('clone', detail)}>复制</button>
          {actions.save.visible ? <button className="btn primary" type="button" disabled={!actions.save.enabled || Boolean(mutation)} title={actions.save.reason || undefined} onClick={onSave}>{mutation === `save:${detail.id}` ? '保存中' : '保存草稿'}</button> : null}
          {actions.activate.visible ? <button className="btn primary" type="button" disabled={Boolean(mutation)} onClick={() => onAction('activate', detail)}>设为生效</button> : null}
        </div>
      </div>

      {!editable ? <div className="prompt-readonly">{readOnlyMessage || '此版本只读，修改请复制为新草稿。'}</div> : null}

      <div className="prompt-admin-detail-layout">
        <main className="prompt-admin-editor">
          <label className="prompt-change-note"><span>变更说明</span><input value={detail.changeNote || ''} readOnly={!editable} onChange={event => onChange({ ...detail, changeNote: event.target.value })} /></label>

          <div className="prompt-editor-section">
            <h3>System Prompt</h3>
            <div className="prompt-parts">
              {systemBlocks.map((block, index) => (
                <details className={`prompt-part ${block.key === 'system_output_contract_prompt' ? 'contract' : ''}`} open={index === 0} key={block.key}>
                  <summary><span><b id={`prompt-part-${detail.id}-${block.key}-label`}>{index + 1}. {block.title || block.key}</b><code>{block.key}</code></span><span>{block.key === 'system_output_contract_prompt' ? '最后渲染' : '展开'}</span></summary>
                  <PromptMarkdownField
                    labelId={`prompt-part-${detail.id}-${block.key}-label`}
                    rows={promptMarkdownRows(block.content)}
                    value={block.content || ''}
                    readOnly={!editable}
                    onChange={content => updateBlock(block.key, content)}
                  />
                </details>
              ))}
            </div>
          </div>

          <section className="prompt-editor-section">
            <h3 id={`prompt-user-template-${detail.id}-label`}>User Template</h3>
            <PromptMarkdownField
              labelId={`prompt-user-template-${detail.id}-label`}
              rows="10"
              value={detail.userPromptTemplate || ''}
              readOnly={!editable}
              onChange={userPromptTemplate => onChange({ ...detail, userPromptTemplate })}
            />
          </section>

          <div className="prompt-editor-grid">
            <section className="prompt-editor-section">
              <h3 id={`prompt-render-order-${detail.id}-label`}>System 编排顺序</h3>
              <textarea aria-labelledby={`prompt-render-order-${detail.id}-label`} className="code-area" rows="7" value={detail.systemRenderOrderText || ''} readOnly={!editable} onChange={event => onChange({ ...detail, systemRenderOrderText: event.target.value })} />
            </section>
            <section className="prompt-editor-section">
              <h3 id={`prompt-variables-${detail.id}-label`}>变量声明</h3>
              <textarea aria-labelledby={`prompt-variables-${detail.id}-label`} className="code-area" rows="7" value={detail.variablesSchemaText || ''} readOnly={!editable} onChange={event => onChange({ ...detail, variablesSchemaText: event.target.value })} />
            </section>
          </div>
          <section className="prompt-editor-section">
            <h3 id={`prompt-output-${detail.id}-label`}>输出结构</h3>
            <textarea aria-labelledby={`prompt-output-${detail.id}-label`} className="code-area" rows="12" value={detail.outputSchemaText || ''} readOnly={!editable} onChange={event => onChange({ ...detail, outputSchemaText: event.target.value })} />
          </section>
        </main>

        <aside className="prompt-admin-side">
          <PromptTestPanel
            key={detail.id}
            detail={detail}
            apiClient={apiClient}
            pending={mutation === `test:${detail.id}`}
            blocked={Boolean(mutation) && mutation !== `test:${detail.id}`}
            error={testError}
            onTest={onTest}
          />
        </aside>
      </div>
    </section>
  )
}

function PromptMarkdownField({ labelId, rows, value, readOnly, onChange }) {
  const [mode, setMode] = useState(readOnly ? 'preview' : 'edit')
  const text = String(value || '')
  useEffect(() => {
    if (readOnly && mode === 'edit') setMode('preview')
  }, [mode, readOnly])
  return (
    <div className="prompt-markdown-field">
      {readOnly ? (
        <div className="prompt-markdown-toolbar readonly"><span>Markdown 预览</span></div>
      ) : (
        <div className="prompt-markdown-toolbar" role="group" aria-label="Markdown 显示模式">
          <button className={mode === 'edit' ? 'active' : ''} type="button" onClick={() => setMode('edit')}>编辑</button>
          <button className={mode === 'preview' ? 'active' : ''} type="button" onClick={() => setMode('preview')}>预览</button>
        </div>
      )}
      {mode === 'edit' ? (
        <textarea
          aria-labelledby={labelId}
          rows={rows}
          value={text}
          readOnly={readOnly}
          onChange={event => onChange(event.target.value)}
        />
      ) : (
        <PromptMarkdownPreview content={text} />
      )}
    </div>
  )
}

function promptMarkdownRows(content) {
  return Math.min(14, Math.max(5, String(content || '').split('\n').length + 2))
}

function PromptMarkdownPreview({ content }) {
  const blocks = parsePromptMarkdown(content)
  if (!blocks.length) return <div className="prompt-markdown-preview empty">暂无内容</div>
  return (
    <div className="prompt-markdown-preview">
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          const Heading = `h${Math.min(6, Math.max(1, block.level))}`
          return <Heading key={index}>{block.text}</Heading>
        }
        if (block.type === 'code') {
          return <pre key={index}><code>{block.text}</code></pre>
        }
        if (block.type === 'list') {
          const List = block.ordered ? 'ol' : 'ul'
          return <List key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</List>
        }
        if (block.type === 'quote') {
          return <blockquote key={index}>{block.lines.map((line, lineIndex) => <p key={lineIndex}>{line}</p>)}</blockquote>
        }
        return <p key={index}>{block.text}</p>
      })}
    </div>
  )
}

function parsePromptMarkdown(content) {
  const lines = String(content || '').replace(/\r\n/g, '\n').split('\n')
  const blocks = []
  let index = 0
  while (index < lines.length) {
    const line = lines[index]
    if (!line.trim()) {
      index += 1
      continue
    }
    const fence = line.match(/^```/)
    if (fence) {
      const code = []
      index += 1
      while (index < lines.length && !/^```/.test(lines[index])) {
        code.push(lines[index])
        index += 1
      }
      if (index < lines.length) index += 1
      blocks.push({ type: 'code', text: code.join('\n') })
      continue
    }
    const heading = line.match(/^(#{1,6})\s+(.+)$/)
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2].trim() })
      index += 1
      continue
    }
    const unordered = line.match(/^\s*[-*]\s+(.+)$/)
    const ordered = line.match(/^\s*\d+[.)]\s+(.+)$/)
    if (unordered || ordered) {
      const items = []
      const isOrdered = Boolean(ordered)
      while (index < lines.length) {
        const item = isOrdered
          ? lines[index].match(/^\s*\d+[.)]\s+(.+)$/)
          : lines[index].match(/^\s*[-*]\s+(.+)$/)
        if (!item) break
        items.push(item[1].trim())
        index += 1
      }
      blocks.push({ type: 'list', ordered: isOrdered, items })
      continue
    }
    const quote = line.match(/^\s*>\s?(.*)$/)
    if (quote) {
      const quoteLines = []
      while (index < lines.length) {
        const next = lines[index].match(/^\s*>\s?(.*)$/)
        if (!next) break
        quoteLines.push(next[1].trim())
        index += 1
      }
      blocks.push({ type: 'quote', lines: quoteLines })
      continue
    }
    const paragraph = [line.trim()]
    index += 1
    while (index < lines.length && lines[index].trim() && !isPromptMarkdownBoundary(lines[index])) {
      paragraph.push(lines[index].trim())
      index += 1
    }
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') })
  }
  return blocks
}

function isPromptMarkdownBoundary(line) {
  return /^```/.test(line) ||
    /^(#{1,6})\s+/.test(line) ||
    /^\s*[-*]\s+/.test(line) ||
    /^\s*\d+[.)]\s+/.test(line) ||
    /^\s*>\s?/.test(line)
}

function PromptTestPanel({ detail, apiClient, pending, blocked, error, onTest }) {
  const [input, setInput] = useState(() => promptTestInputTemplate(detail))
  const [noteId, setNoteId] = useState('')
  const [noteLoading, setNoteLoading] = useState(false)
  const [noteError, setNoteError] = useState('')
  const [intentQuery, setIntentQuery] = useState('')
  const [intentPools, setIntentPools] = useState([])
  const [intentPoolId, setIntentPoolId] = useState('')
  const [intentPoolLoading, setIntentPoolLoading] = useState(false)
  const [intentError, setIntentError] = useState('')
  const isIntentPrompt = detail.promptNodeKey === 'intent_parse'
  const isImagePrompt = detail.promptNodeKey === 'image_analyze'
  const selectableIntentPools = promptSelectablePools(intentPools)
  useEffect(() => {
    if (!isIntentPrompt) return undefined
    let alive = true
    setIntentPoolLoading(true)
    setIntentError('')
    apiClient.get('/content-learning/pools')
      .then(response => {
        if (!alive) return
        const rows = pickPromptRows(response)
        setIntentPools(rows)
        setIntentPoolId(current => current || String(promptSelectablePools(rows)[0]?.id || ''))
      })
      .catch(cause => {
        if (alive) setIntentError(apiMessage(cause, '内容池加载失败'))
      })
      .finally(() => {
        if (alive) setIntentPoolLoading(false)
      })
    return () => {
      alive = false
    }
  }, [apiClient, isIntentPrompt])
  function fillFromIntentQuery(query = intentQuery) {
    const normalized = query.trim()
    if (!normalized) {
      setIntentError('请输入查询内容')
      return
    }
    const pool = selectableIntentPools.find(item => String(item.id) === String(intentPoolId))
    if (!pool) {
      setIntentError(intentPoolLoading ? '内容池加载中' : '请选择内容池')
      return
    }
    setIntentError('')
    setIntentQuery(normalized)
    setInput(JSON.stringify(promptIntentTestInput(normalized, pool), null, 2))
  }
  async function fillFromNote() {
    const normalized = noteId.trim()
    if (!normalized) {
      setNoteError('请输入 note_id')
      return
    }
    setNoteLoading(true)
    setNoteError('')
    try {
      const response = await apiClient.get('/urls/get-note-detail', { params: { note_id: normalized } })
      const note = pickPromptNote(response)
      if (!note) throw new Error('未找到这篇笔记')
      setInput(JSON.stringify(promptImageTestInputFromNote(note), null, 2))
    } catch (cause) {
      setNoteError(apiMessage(cause, '笔记填充失败'))
    } finally {
      setNoteLoading(false)
    }
  }
  return (
    <section className="prompt-test-section" aria-labelledby={`prompt-test-${detail.id}-title`}>
      <div className="prompt-test-input">
        <div className="prompt-test-heading">
          <h3 id={`prompt-test-${detail.id}-title`}>版本测试</h3>
          <button className="btn primary" type="button" disabled={pending || blocked} onClick={() => onTest(detail, input)}>
            {pending ? '测试中' : '运行测试'}
          </button>
        </div>
        {isImagePrompt ? (
          <div className="prompt-note-fill">
            <label htmlFor={`prompt-test-${detail.id}-note-id`}>按笔记填充</label>
            <div>
              <input
                id={`prompt-test-${detail.id}-note-id`}
                value={noteId}
                disabled={pending || noteLoading}
                placeholder="输入 note_id"
                onChange={event => setNoteId(event.target.value)}
              />
              <button className="btn ghost sm" type="button" disabled={pending || noteLoading} onClick={fillFromNote}>{noteLoading ? '填充中' : '填充'}</button>
            </div>
            {noteError ? <span role="alert">{noteError}</span> : null}
          </div>
        ) : null}
        {isIntentPrompt ? (
          <div className="prompt-intent-fill">
            <div className="prompt-intent-fields">
              <label className="prompt-intent-field pool" htmlFor={`prompt-test-${detail.id}-intent-pool`}>
                <span>内容池</span>
                <select
                  id={`prompt-test-${detail.id}-intent-pool`}
                  value={intentPoolId}
                  disabled={pending || intentPoolLoading || !selectableIntentPools.length}
                  onChange={event => setIntentPoolId(event.target.value)}
                >
                  <option value="">{intentPoolLoading ? '加载中' : '请选择'}</option>
                  {selectableIntentPools.map(pool => (
                    <option value={pool.id} key={pool.id}>
                      {pool.name || `内容池 #${pool.id}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="prompt-intent-field query" htmlFor={`prompt-test-${detail.id}-intent-query`}>
                <span>查询内容</span>
                <input
                  id={`prompt-test-${detail.id}-intent-query`}
                  value={intentQuery}
                  disabled={pending}
                  placeholder="输入自然语言查询"
                  onChange={event => setIntentQuery(event.target.value)}
                />
              </label>
              <button className="btn ghost sm" type="button" disabled={pending || intentPoolLoading || !selectableIntentPools.length} onClick={() => fillFromIntentQuery()}>填充</button>
            </div>
            <div className="prompt-intent-presets" aria-label="查询样例">
              {PROMPT_INTENT_TEST_PRESETS.map(preset => (
                <button
                  className="chip"
                  type="button"
                  disabled={pending || intentPoolLoading || !selectableIntentPools.length}
                  key={preset.label}
                  onClick={() => fillFromIntentQuery(preset.query)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {intentError ? <span role="alert">{intentError}</span> : null}
          </div>
        ) : null}
        <label htmlFor={`prompt-test-${detail.id}-input`}>测试输入 (JSON)</label>
        <textarea
          id={`prompt-test-${detail.id}-input`}
          className="code-area"
          rows="8"
          value={input}
          disabled={pending}
          aria-describedby={error ? `prompt-test-${detail.id}-error` : undefined}
          onChange={event => setInput(event.target.value)}
        />
        {error ? <div className="prompt-test-error" id={`prompt-test-${detail.id}-error`} role="alert">{error}</div> : null}
      </div>
      <div className="prompt-test-result" aria-live="polite" aria-busy={pending}>
        <h3>最近测试结果</h3>
        {pending ? <div className="prompt-test-pending">正在等待 Provider 返回...</div> : <PromptTestSummary result={detail.lastTestResult} />}
      </div>
    </section>
  )
}

function PromptTestSummary({ result, compact = false }) {
  const state = promptTestResultState(result)
  if (!result) return <span className="prompt-test-empty">{state.label}</span>
  const output = promptTestOutput(result)
  if (compact) {
    return (
      <span className={`prompt-test-compact ${state.tone}`} title={result.errorMessage || undefined}>
        <b>{state.label}</b>
        <span>{Number(result.latencyMs || 0)} ms{result.errorCode ? ` · ${result.errorCode}` : ''}</span>
      </span>
    )
  }
  return (
    <div className={`prompt-test-summary ${state.tone}`}>
      <div className="prompt-test-summary-head">
        <span className={`prompt-status ${state.tone}`}>{state.label}</span>
        <span>{formatTime(result.createdAt)}</span>
      </div>
      <section className="prompt-test-output" aria-labelledby={`prompt-test-output-${result.invocationId || 'latest'}`}>
        <h4 id={`prompt-test-output-${result.invocationId || 'latest'}`}>模型理解</h4>
        {output.hasOutput ? (
          <PromptTestOutputMeaning output={output.json} />
        ) : (
          <p>{result.errorCode ? '本次调用未返回可用输出。' : '暂无输出内容。'}</p>
        )}
      </section>
      <dl>
        <div><dt>Schema</dt><dd>{result.schemaValid ? '有效' : '无效'}</dd></div>
        <div><dt>耗时</dt><dd>{Number(result.latencyMs || 0)} ms</dd></div>
        <div><dt>Provider</dt><dd>{[result.provider, result.model].filter(Boolean).join(' / ') || '-'}</dd></div>
        <div><dt>Trace ID</dt><dd><code>{result.traceId || '-'}</code></dd></div>
        <div><dt>调用序号</dt><dd>{Number(result.attemptNo || 1)}</dd></div>
        <div><dt>Provider Request ID</dt><dd><code>{result.providerRequestId || '-'}</code></dd></div>
        <div><dt>错误</dt><dd>{result.errorCode ? `${result.errorCode}${result.errorMessage ? `: ${result.errorMessage}` : ''}` : '-'}</dd></div>
      </dl>
      {result.input || output.hasOutput ? (
        <div className="prompt-test-rendered-input">
          {result.input ? <details>
            <summary>系统提示词</summary>
            <pre>{result.input.systemPrompt || '-'}</pre>
          </details> : null}
          {result.input ? <details>
            <summary>用户提示词</summary>
            <pre>{result.input.userPrompt || '-'}</pre>
          </details> : null}
          {output.hasOutput ? <details>
            <summary>输出 JSON</summary>
            <pre>{output.content}</pre>
            {output.truncated ? <span>仅展示前 1000 字符</span> : null}
          </details> : null}
        </div>
      ) : null}
    </div>
  )
}

const PROMPT_DIMENSION_LABELS = {
  title_body: '主体文本',
  image: '图片语义',
  comment: '评论反馈',
  ops_review: '运营备注',
}

const PROMPT_INTENT_TEST_PRESETS = [
  {
    label: '减肥自律',
    query: '找减肥自律打卡、壁纸提醒相关笔记，图里有口号或饮食运动元素',
  },
  {
    label: '敏感肌防晒',
    query: '找敏感肌可以参考的防晒教程，评论里有人问刺激或过敏，不要美黑内容',
  },
  {
    label: '前后对比',
    query: '找有前后对比图的变美或护肤效果笔记，标题偏干货教程，排除母婴',
  },
]

function PromptTestOutputMeaning({ output }) {
  if (!output || typeof output !== 'object') {
    return <p>模型返回了文本结果，请展开下方“输出 JSON”查看原文。</p>
  }
  if (isImagePromptOutput(output)) return <PromptImageOutputMeaning output={output} />
  const weights = output.dimensionWeights && typeof output.dimensionWeights === 'object' ? output.dimensionWeights : {}
  const filters = output.filters && typeof output.filters === 'object' ? output.filters : {}
  const includeTagIds = Array.isArray(filters.includeTagIds) ? filters.includeTagIds : []
  const excludeTagIds = Array.isArray(filters.excludeTagIds) ? filters.excludeTagIds : []
  return (
    <div className="prompt-output-meaning">
      <dl className="prompt-output-brief">
        <div>
          <dt>要找的内容</dt>
          <dd>{String(output.positiveText || '').trim() || '-'}</dd>
        </div>
        <div>
          <dt>排除内容</dt>
          <dd>{String(output.negativeText || '').trim() || '无'}</dd>
        </div>
        <div>
          <dt>理解可信度</dt>
          <dd>{confidenceText(output.confidence)}<span>{confidenceHint(output.confidence)}</span></dd>
        </div>
      </dl>

      <div className="prompt-output-weights">
        <h5>检索维度权重</h5>
        {Object.entries(PROMPT_DIMENSION_LABELS).map(([key, label]) => {
          const value = normalizedWeight(weights[key])
          return (
            <div className="prompt-output-weight" key={key}>
              <span>{label}</span>
              <i><b style={{ width: `${Math.round(value * 100)}%` }} /></i>
              <em>{Math.round(value * 100)}%</em>
            </div>
          )
        })}
      </div>

      <div className="prompt-output-filter-line">
        <span>标签过滤</span>
        <b>{includeTagIds.length || excludeTagIds.length
          ? `${includeTagIds.length ? `包含 ${includeTagIds.join(', ')}` : ''}${includeTagIds.length && excludeTagIds.length ? '；' : ''}${excludeTagIds.length ? `排除 ${excludeTagIds.join(', ')}` : ''}`
          : '无'}</b>
      </div>
    </div>
  )
}

function PromptImageOutputMeaning({ output }) {
  const searchText = String(output.searchText || '').trim()
  const images = Array.isArray(output.images) ? output.images : []
  return (
    <div className="prompt-output-meaning image">
      <dl className="prompt-output-brief">
        <div>
          <dt>图片语义摘要</dt>
          <dd>{searchText || '-'}</dd>
        </div>
        <div>
          <dt>图片数量</dt>
          <dd>{images.length}</dd>
        </div>
      </dl>
      <div className="prompt-output-images">
        <h5>逐图摘要</h5>
        {images.length ? images.map((item, index) => (
          <div className="prompt-output-image-row" key={`${item?.index ?? index}-${index}`}>
            <span>#{Number.isFinite(Number(item?.index)) ? Number(item.index) : index}</span>
            <p>{String(item?.summary || '').trim() || '无可靠信息'}</p>
          </div>
        )) : <p>未返回逐图摘要。</p>}
      </div>
    </div>
  )
}

function isImagePromptOutput(output) {
  return Object.prototype.hasOwnProperty.call(output, 'searchText') || Array.isArray(output.images)
}

function promptTestOutput(result) {
  const summary = result?.outputSummary || {}
  const json = summary.json ?? result?.outputJson ?? result?.output
  const text = summary.textPreview || result?.outputText || result?.rawText || ''
  const hasJson = json && typeof json === 'object' && Object.keys(json).length > 0
  if (hasJson) {
    return {
      hasOutput: true,
      content: JSON.stringify(json, null, 2),
      json,
      truncated: Boolean(summary.truncated),
    }
  }
  if (text) {
    return {
      hasOutput: true,
      content: String(text),
      json: null,
      truncated: Boolean(summary.truncated),
    }
  }
  return { hasOutput: false, content: '', json: null, truncated: false }
}

function normalizedWeight(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 0
  return Math.max(0, Math.min(1, number))
}

function confidenceText(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return '-'
  return `${Math.round(Math.max(0, Math.min(1, number)) * 100)}%`
}

function confidenceHint(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return '未返回'
  if (number >= 0.8) return '意图清晰'
  if (number >= 0.5) return '可用，建议复核'
  return '偏低，需要补充 query'
}

function PromptTableSkeleton() {
  return <div className="prompt-skeleton" aria-label="正在加载 Prompt 版本">{[1, 2, 3, 4].map(row => <i key={row} />)}</div>
}

function PromptDetailSkeleton() {
  return <div className="prompt-admin-detail prompt-skeleton detail" aria-label="正在加载 Prompt 详情"><i /><i /><i /></div>
}

function prepareDetail(value) {
  if (!value || value.variablesSchemaText !== undefined) return value
  return {
    ...value,
    systemPromptBlocks: Array.isArray(value.systemPromptBlocks) ? value.systemPromptBlocks : [],
    systemRenderOrderText: JSON.stringify(value.systemRenderOrder || [], null, 2),
    userPromptTemplate: value.userPromptTemplate || '',
    variablesSchemaText: JSON.stringify(value.variablesSchema || {}, null, 2),
    outputSchemaText: JSON.stringify(value.outputSchema || {}, null, 2),
  }
}

function pickPromptNote(response) {
  const data = response?.data?.data ?? response?.data ?? response
  if (Array.isArray(data)) return data[0] || null
  if (Array.isArray(data?.data)) return data.data[0] || null
  if (Array.isArray(data?.list)) return data.list[0] || null
  return data && typeof data === 'object' ? data : null
}

function pickPromptRows(response) {
  const data = response?.data?.data ?? response?.data ?? response
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.list)) return data.list
  if (Array.isArray(data?.rows)) return data.rows
  return []
}

function promptImageTestInputFromNote(note) {
  const media = normalizePromptMediaContent(note.mediaContent, note.coverImage || note.cover || '')
  const urls = uniquePromptList([media.cover, ...(media.images || [])])
  if (!urls.length) throw new Error('这篇笔记没有可测试的图片')
  return {
    note: {
      noteId: note.note_id || note.noteId || '',
      title: note.title || '',
      description: note.description || '',
    },
    image_count: urls.length,
    images: urls.map((url, index) => ({ index, url })),
  }
}

function promptIntentTestInput(query, pool) {
  return {
    query,
    pool_profile: promptPoolProfile(pool),
    enabled_dimensions: ['title_body', 'image', 'comment', 'ops_review'],
  }
}

function promptSelectablePools(pools) {
  const rows = Array.isArray(pools) ? pools : []
  const active = rows.filter(isPromptPoolActive)
  return active.length ? active : rows
}

function isPromptPoolActive(pool) {
  if (typeof pool?.status === 'string') return pool.status === 'active'
  return Number(pool?.status ?? 1) === 1
}

function promptPoolProfile(pool) {
  const noteCount = Number(pool?.noteCount || 0)
  const approvedCount = Number(pool?.approvedCount || 0)
  const reviewCount = Number(pool?.reviewCount || 0)
  const rejectedCount = Number(pool?.rejectedCount || 0)
  return [
    `内容池 ID: ${pool?.id || ''}`,
    `名称: ${pool?.name || '-'}`,
    `描述: ${String(pool?.description || '').trim() || '无'}`,
    `状态: ${promptPoolStatusLabel(pool?.status)}`,
    `样本统计: 总计 ${noteCount}，已通过 ${approvedCount}，待审核 ${reviewCount}，已拒绝 ${rejectedCount}`,
  ].join('\n')
}

function promptPoolStatusLabel(status) {
  if (status === 'draft' || Number(status) === 0) return '草稿'
  if (status === 'archived' || Number(status) === 9) return '已归档'
  if (status === 'active' || status === undefined || Number(status) === 1) return '已发布'
  return String(status || '-')
}

function promptTestImageUrls(variables) {
  if (!variables || typeof variables !== 'object') return []
  const images = Array.isArray(variables.images) ? variables.images : []
  return uniquePromptList(images.map(item => typeof item === 'string' ? item : item?.url))
}

function normalizePromptMediaContent(media, fallbackCover = '') {
  if (typeof media === 'string') media = safePromptJson(media)
  const fromItems = Array.isArray(media?.items)
    ? media.items.filter(item => !['video'].includes(String(item?.type || ''))).map(item => item?.url || item?.src).filter(Boolean)
    : []
  const arrayItems = Array.isArray(media) ? media.map(item => typeof item === 'string' ? item : item?.url || item?.src).filter(Boolean) : []
  const images = uniquePromptList([
    ...(Array.isArray(media?.images) ? media.images : []),
    ...(Array.isArray(media?.imageList) ? media.imageList : []),
    ...fromItems,
    ...arrayItems,
  ])
  const cover = media?.cover || fallbackCover || images[0] || ''
  return { cover, images }
}

function safePromptJson(value) {
  if (!value || typeof value !== 'string') return value || null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function uniquePromptList(rows) {
  return Array.from(new Set((rows || []).map(item => String(item || '').trim()).filter(Boolean)))
}

function apiMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback
}

function formatTime(value) {
  const time = Number(value || 0)
  if (!time) return '-'
  return new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(time))
}

function promptNodeScope(node) {
  return `${node.scenarioKey}:${node.promptNodeKey}`
}

function handleTabKeyDown(event) {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
  const tabs = Array.from(event.currentTarget.parentElement.querySelectorAll('[role="tab"]')).filter(tab => !tab.disabled)
  const index = tabs.indexOf(event.currentTarget)
  if (index < 0 || !tabs.length) return
  event.preventDefault()
  const nextIndex = event.key === 'Home'
    ? 0
    : event.key === 'End'
      ? tabs.length - 1
      : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length
  tabs[nextIndex].focus()
  tabs[nextIndex].click()
}
